"use client";

import type React from "react";
import { useCallback, useContext, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import { toast } from "react-toastify";

import { updateVisibility } from "src/redux/modals/slice";
import { useAppDispatch, useAppSelector } from "src/hooks/redux-hooks";
import { openInANewTab } from "src/helpers/utils";
import { AuthContext } from "src/context/auth-context/auth-context";
import { LookupContext } from "src/context/lookup-context/lookup-context";
import type { UserDetails } from "src/types/auth";
import { updateProfile } from "src/services/profile";

const InvoicedRedirectModal = () => {
  const dispatch = useAppDispatch();
  const [userDetails, setUserDetails] = useContext(AuthContext);
  const lookupContext = useContext(LookupContext) as Record<
    string,
    unknown
  > | null;
  const propertyManagerSettingsLookupObj =
    lookupContext?.propertyManagerSettingsLookupObj as
      | Record<string, Record<string, unknown>>
      | undefined;

  const [isLoading, setIsLoading] = useState(false);
  const [rememberPreference, setRememberPreference] = useState(false);

  const invoicedUrl = useAppSelector(
    (state) => state?.modals?.invoicedRedirectModal?.extraProps?.invoicedUrl,
  ) as string;

  const handleModalClose = useCallback(() => {
    dispatch(
      updateVisibility({
        modalType: "invoicedRedirectModal",
        isVisible: false,
      }),
    );
  }, [dispatch]);

  const handleCancel = useCallback(() => {
    handleModalClose();
  }, [handleModalClose]);

  const handleOpenInvoiced = useCallback(() => {
    if (!invoicedUrl) {
      toast.error("Invoiced URL is not available");
      return;
    }

    // If checkbox is checked, save preference first
    if (rememberPreference) {
      setIsLoading(true);
      const payLoad = {
        property_manager_setting_ids: [
          propertyManagerSettingsLookupObj?.[
            "prefer-direct-invoice-provider-navigation"
          ]?.property_manager_setting_id,
        ],
      };
      updateProfile(payLoad)
        .then(() => {
          setUserDetails(
            (prevDetails: UserDetails | null) =>
              ({
                ...prevDetails,
                propertyManagerSettingsObj: {
                  ...userDetails?.propertyManagerSettingsObj,
                  "prefer-direct-invoice-provider-navigation":
                    propertyManagerSettingsLookupObj?.[
                      "prefer-direct-invoice-provider-navigation"
                    ],
                },
              }) as unknown as UserDetails,
          );
          toast("Preference saved", { type: "success", theme: "colored" });
          // Open Invoiced in new tab after saving
          openInANewTab(invoicedUrl);

          setIsLoading(false);
          // Close modal
          handleModalClose();
        })
        .catch(() => {
          setIsLoading(false);
        });
    } else {
      // If checkbox is not checked, just open URL in new tab
      openInANewTab(invoicedUrl as string);

      handleModalClose();
    }
  }, [
    handleModalClose,
    invoicedUrl,
    propertyManagerSettingsLookupObj,
    rememberPreference,
    setUserDetails,
    userDetails?.propertyManagerSettingsObj,
  ]);

  return (
    <Modal
      isOpen
      toggle={handleModalClose}
      centered
      className="global-modal invoiced-redirect-modal"
    >
      <ModalHeader toggle={handleModalClose}>
        Invoices are now handled in Invoiced
      </ModalHeader>
      <ModalBody>
        <div className="invoiced-modal-content">
          <p>
            Lula has partnered with Invoiced to provide a better experience
            {' '}
            for its Invoicing process.
            {' '}
            With invoiced you can view all your invoices and pay them
            {' '}
            without the need to submit manual remittances.
            {' '}
            The accounting contacts at your company can access the portal
            {' '}
            with just their email.
            {' '}
            If additional people in your company need access to the portal, contact
            {' '}
            <a href="mailto:payments@lula.life">
              payments@lula.life
            </a>
            .
            <br />
            <a
              href="https://help.lula.life/property-managers/en/articles/13379491-lula-updated-payment-process"
              className="invoiced-link"
            >
              Learn more about Invoiced
            </a>
          </p>
          <div className="action-btns">
            <Button
              color="secondary"
              onClick={handleCancel}
              className="w-100"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleOpenInvoiced}
              className="w-100"
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Open Invoiced"}
            </Button>
          </div>
          <FormGroup check>
            <Label check>
              <Input
                type="checkbox"
                checked={rememberPreference}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setRememberPreference(e.target.checked)
                }
                disabled={isLoading}
              />{" "}
              In the future, take me directly to Invoiced
            </Label>
          </FormGroup>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default InvoicedRedirectModal;
